import { isLiteTemplate } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Group, Progress, Tooltip } from "@mantine/core"
import { IconChecklist, IconClock, IconEyeOff, IconFlag } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { useMemo } from "react"
import { canReprocess } from "../../lib/meeting-reprocess"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { rosterSpeakerColorMap } from "../../lib/speaker-colors"
import { AvatarStack } from "../AvatarStack"
import { MeetingCardKebab } from "../MeetingCardKebab"
import { RecordingModeChip } from "../RecordingModeChip"
import { StatusPill } from "../StatusPill"
import { TalkShare } from "../TalkShare"
import { TopicChips } from "../TopicChips"
import {
  formatShortDate,
  formatTime,
  getSourceIcon,
  isNamedSpeaker,
  meetingErrorReason,
  SENTIMENT_CHROME,
  sentimentLabel,
  speakerFirstName,
  stageLabel,
} from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/** How many named people get their first name written out next to the avatars. */
const NAMED_SPEAKER_LABELS = 3

/**
 * MeetingCard — the primary editorial card in the meeting list grid. Pure
 * presentational: renders the meeting summary (time, sentiment, source,
 * status, topics, talk-share, speakers, action/decision counts) and wraps it
 * in a router `<Link>` to the detail view. Kebab actions bubble out via
 * `onAction(meetingId, action)`; the host owns navigation / modals / mutations.
 */
export const MeetingCard = ({
  meeting,
  variant = "full",
  showDate,
  canManage,
  canDeletePermanently,
  onAction,
  processing,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  // Same slot order the meeting page colours by. The list has no saved overrides.
  const speakerColors = useMemo(() => rosterSpeakerColorMap(meeting.speaker_names ?? []), [meeting.speaker_names])
  // Recording mode is per meeting — StatusPill + stats + the chip key off template_id.
  // Viewer `variant` is the list chrome (Start recording vs Start meeting), not this card.
  const recordedLite = isLiteTemplate(meeting.template_id)
  // Reprocess is offered for any finished/failed meeting (not just summarized) —
  // shared semantics with the detail view via `canReprocess`.
  const reprocessable = canReprocess(meeting.status)
  const sentiment = meeting.sentiment ? (SENTIMENT_CHROME[meeting.sentiment] ?? null) : null
  const accent = sentiment?.cssColor ?? "var(--mantine-color-brand-5)"
  const sentimentInk = sentiment?.textColor ?? "var(--lex-text-brand)"
  const speakers = meeting.speaker_names ?? []
  const namedSpeakers = speakers.filter(isNamedSpeaker).slice(0, NAMED_SPEAKER_LABELS)
  const time = formatTime(i18n, meeting.start_time)
  const date = formatShortDate(i18n, meeting.start_time)
  const meta = showDate ? `${date} · ${time}` : time
  const processingLabel = processing ? stageLabel(processing.stage, i18n) : null
  const errorReason = meeting.status === "error" ? meetingErrorReason(meeting, i18n) : null
  const showStatus = meeting.status !== "summarized"
  const showActions = !recordedLite && meeting.action_count !== undefined && meeting.action_count > 0
  const showDecisions = !recordedLite && meeting.decision_count !== undefined && meeting.decision_count > 0
  const showFoot = speakers.length > 0 || showActions || showDecisions
  const showMeta = showStatus || recordedLite || Boolean(errorReason)
  const hasAnalysis =
    (meeting.topics?.length ?? 0) > 0 ||
    (meeting.talk_share?.length ?? 0) > 0 ||
    (meeting.action_count ?? 0) > 0 ||
    (meeting.decision_count ?? 0) > 0
  const compact = !processing && !hasAnalysis

  return (
    <Link
      to="/meetings/$id"
      params={{ id: meeting.id }}
      className={`${classes.card} lex-surface-card ${meeting.hidden ? classes.cardHidden : ""} ${processing ? classes.cardGhost : ""} ${compact ? classes.cardCompact : ""}`}
      data-testid="meeting-row"
      data-list-variant={variant}
      data-recorded-lite={recordedLite ? "true" : undefined}
      data-compact={compact ? "true" : undefined}
      style={{ ["--lk-accent" as string]: accent, ["--lk-sentiment-ink" as string]: sentimentInk }}
    >
      <div className={classes.cardRow}>
        <span className={classes.cardTime}>{meta}</span>
        {sentiment && meeting.sentiment && (
          <>
            <span className="lex-dot" />
            <span className={classes.cardSentiment}>
              {sentiment.icon}
              {sentimentLabel(meeting.sentiment, i18n)}
            </span>
          </>
        )}
        {meeting.duration_minutes && meeting.duration_minutes > 0 && (
          <>
            <span className="lex-dot" />
            <span className={classes.cardMeta}>
              <IconClock size={12} />
              {meeting.duration_minutes}m
            </span>
          </>
        )}
        {meeting.source && meeting.source !== "unknown" && getSourceIcon(meeting.source) && (
          <>
            <span className="lex-dot" />
            <span className={classes.cardMeta}>{getSourceIcon(meeting.source)}</span>
          </>
        )}
      </div>
      <div className={classes.cardKebab}>
        <MeetingCardKebab
          hidden={meeting.hidden}
          canManage={canManage}
          canDeletePermanently={canDeletePermanently}
          canReprocess={reprocessable}
          variant={recordedLite ? "lite" : "full"}
          onAction={(action) => onAction(meeting.id, action)}
        />
      </div>

      <Group gap={6} wrap="nowrap" className={classes.cardTitleRow}>
        <div className={classes.cardTitle}>
          {displayMeetingTitle(meeting.subject, meeting.topics, i18n, {
            lite: recordedLite,
            startedAt: meeting.start_time,
          })}
        </div>
        {meeting.hidden && (
          <Tooltip label={i18n._(msg`Hidden from users`)} withArrow>
            <Badge size="xs" variant="light" color="gray" leftSection={<IconEyeOff size={10} />}>
              <Trans>hidden</Trans>
            </Badge>
          </Tooltip>
        )}
      </Group>

      <TopicChips topics={meeting.topics ?? []} max={4} size="sm" />

      {meeting.talk_share && meeting.talk_share.length > 0 && (
        <TalkShare share={meeting.talk_share} speakerNames={meeting.speaker_names} colorMap={speakerColors} />
      )}

      {processing && (
        <div className={classes.cardGhostProgress}>
          <Progress size="xs" radius="xl" color="brand" value={processing.progress} />
          <div className={classes.cardGhostMeta}>
            {processingLabel && <span className={classes.cardGhostStage}>{processingLabel}</span>}
            {processingLabel && <span className="lex-dot" />}
            <span className={classes.cardGhostPct}>{Math.round(processing.progress)}%</span>
            {processing.currentStepName && <span className={classes.cardGhostStep}>{processing.currentStepName}</span>}
          </div>
        </div>
      )}

      {showFoot ? (
        <div className={classes.cardFoot}>
          {speakers.length > 0 && (
            <div className={classes.cardPeople}>
              <AvatarStack names={speakers} colorMap={speakerColors} />
              {namedSpeakers.length > 0 ? (
                <span className={classes.cardNames} data-testid="speaker-names">
                  {namedSpeakers.map(speakerFirstName).join(", ")}
                </span>
              ) : null}
            </div>
          )}
          {(showActions || showDecisions) && (
            <div className={classes.cardStats}>
              {showActions && (
                <span className={classes.cardStat}>
                  <IconChecklist size={12} color="var(--mantine-color-dimmed)" />
                  <strong>{meeting.action_count}</strong> <Trans>actions</Trans>
                </span>
              )}
              {showDecisions && (
                <span className={classes.cardStat}>
                  <IconFlag size={12} color="var(--mantine-color-dimmed)" />
                  <strong>{meeting.decision_count}</strong> <Trans>decisions</Trans>
                </span>
              )}
            </div>
          )}
        </div>
      ) : null}

      {showMeta ? (
        <div className={classes.cardMetaBar}>
          {showStatus ? <StatusPill phase={meeting.status} lite={recordedLite} /> : null}
          {recordedLite ? <RecordingModeChip mode="lite" /> : null}
          {errorReason ? (
            <span className={classes.cardError} title={errorReason}>
              {errorReason}
            </span>
          ) : null}
        </div>
      ) : null}
    </Link>
  )
}
